import { isConnectionType, type ConnectionType } from '@/types/gtm-agents';

/**
 * Where the agent workspace is, and how that reads in the URL. The Agent tab
 * is the bare URL; the other tabs, a drill-in inside a tab (`view`), a run
 * or card of the play and a play proposal are search params, so each links
 * and Back steps out of a drill-in, a run or a proposal.
 */

export const WORKSPACE_TABS = [
  'agent',
  'triggers',
  'play',
  'runs',
  'approvals',
  'settings',
] as const;

export type WorkspaceTab = (typeof WORKSPACE_TABS)[number];

export const TAB_LABEL: Record<WorkspaceTab, string> = {
  agent: 'Agent',
  triggers: 'Triggers',
  play: 'Play',
  runs: 'Runs',
  approvals: 'Approvals',
  settings: 'Settings',
};

/** The Settings tab's Access section, as `view` names it. */
export const ACCESS_VIEW = 'access';

export interface WorkspaceLocation {
  tab: WorkspaceTab;
  /** An explicitly selected owned play. Required when the agent has several. */
  play?: string;
  /** A drill-in inside the tab: `instructions`, `connection:salesforce`, `review:open`. */
  view?: string;
  /**
   * A run open on the play, in the Play tab; on the Runs tab's review of
   * duplicates, the scan whose groups it highlights.
   */
  run?: string;
  /** The card of the play the Play tab opens on. */
  node?: string;
  /** A play proposal under review on the Play tab. */
  proposal?: string;
}

/** The search params of `/agents/$agentId`. */
export interface WorkspaceSearch {
  tab?: Exclude<WorkspaceTab, 'agent'>;
  play?: string;
  view?: string;
  run?: string;
  node?: string;
  proposal?: string;
  /** Set by New agent; kept as it is for Clu to act on. */
  new?: boolean;
  /**
   * The agent's chat open in the dock (layout v2): its conversation id. None
   * is the agent's home. Independent of the panel: the tabs keep it.
   */
  chat?: string;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * The search params as the route accepts them. Old links: `tab=history` is
 * the Runs tab, `tab=config` the Agent tab and `tab=access` the Settings tab
 * on its Access section.
 */
export function parseWorkspaceSearch(
  search: Record<string, unknown>
): WorkspaceSearch {
  const out: WorkspaceSearch = {};
  const access = search.tab === 'access';
  const tab =
    search.tab === 'history' ? 'runs' : access ? 'settings' : search.tab;
  if (
    typeof tab === 'string' &&
    tab !== 'agent' &&
    (WORKSPACE_TABS as readonly string[]).includes(tab)
  )
    out.tab = tab as WorkspaceSearch['tab'];
  const view = access ? ACCESS_VIEW : text(search.view);
  if (view) out.view = view;
  const play = text(search.play);
  if (play) out.play = play;
  const run = text(search.run);
  if (run) out.run = run;
  const node = text(search.node);
  if (node) out.node = node;
  const proposal = text(search.proposal);
  if (proposal) out.proposal = proposal;
  if (search.new === true || search.new === 'true') out.new = true;
  const chat = text(search.chat);
  if (chat) out.chat = chat;
  return out;
}

export function locationOf(search: WorkspaceSearch): WorkspaceLocation {
  return {
    tab: search.tab ?? 'agent',
    play: search.play,
    view: search.view,
    run: search.run,
    node: search.node,
    proposal: search.proposal,
  };
}

/** The search params for a location, keeping `new` and the open chat. */
export function searchOf(
  location: WorkspaceLocation,
  fresh?: boolean,
  chat?: string
): WorkspaceSearch {
  const out: WorkspaceSearch = {};
  if (location.tab !== 'agent') out.tab = location.tab;
  if (location.play) out.play = location.play;
  if (location.view) out.view = location.view;
  if (location.run) out.run = location.run;
  if (location.node) out.node = location.node;
  if (location.proposal) out.proposal = location.proposal;
  if (fresh) out.new = true;
  if (chat) out.chat = chat;
  return out;
}

/** The search params with another chat open (none: the agent's home). */
export function withChat(
  search: WorkspaceSearch,
  chat: string | null
): WorkspaceSearch {
  const next: WorkspaceSearch = { ...search };
  delete next.chat;
  if (chat) next.chat = chat;
  return next;
}

/**
 * Opening a drill-in, a run or a proposal is a step forward, so it pushes and
 * Back steps out of it; switching tabs and closing one replace.
 */
export function pushesHistory(
  from: WorkspaceLocation,
  to: WorkspaceLocation
): boolean {
  return (
    (Boolean(to.play) && to.play !== from.play) ||
    (Boolean(to.view) && to.view !== from.view) ||
    (Boolean(to.run) && to.run !== from.run) ||
    (Boolean(to.proposal) && to.proposal !== from.proposal)
  );
}

/** A drill-in, as `view` names it. */
export type WorkspaceView =
  | { kind: 'instructions' }
  | { kind: 'connection'; type: ConnectionType }
  | { kind: 'skill-new' };

/** The Agent tab's drill-in a `view` names; undefined for anything else. */
export function agentViewOf(
  view: string | undefined
): WorkspaceView | undefined {
  if (!view) return undefined;
  if (view === 'instructions') return { kind: 'instructions' };
  if (view === 'skill:new') return { kind: 'skill-new' };
  const [prefix, type] = view.split(':');
  if (prefix === 'connection' && isConnectionType(type))
    return { kind: 'connection', type };
  return undefined;
}

/**
 * A drill-in of the Triggers tab, as `view` names it: the app picker, an app
 * event's form, the Schedule form of a scheduled brief (W2-49), or a
 * trigger's own form.
 */
export type TriggersView =
  | { kind: 'app-picker' }
  | { kind: 'app-form'; event: string }
  | { kind: 'schedule-form' }
  | { kind: 'edit'; triggerId: string };

/**
 * A drill-in of the Runs tab, as `view` names it: the duplicate groups of a
 * cleanup play under review (W5 design 10.3), `review:open` and the like. A
 * `run` beside it highlights one scan's groups.
 */
export type RunsView =
  | { kind: 'review'; filter: string }
  /** A scheduled brief's run: its brief, review line and Open chat (W2-49). */
  | { kind: 'brief'; runId: string };

/** The Runs tab's drill-in a `view` names; undefined for anything else. */
export function runsViewOf(view: string | undefined): RunsView | undefined {
  if (view?.startsWith('brief:') && view.length > 'brief:'.length)
    return { kind: 'brief', runId: view.slice('brief:'.length) };
  if (!view?.startsWith('review:')) return undefined;
  return { kind: 'review', filter: view.slice('review:'.length) || 'open' };
}

/** The Runs tab's review of duplicates, opened on its open groups. */
export const REVIEW_OPEN_VIEW = 'review:open';

/** The Triggers tab's drill-in a `view` names; undefined for anything else. */
export function triggersViewOf(
  view: string | undefined
): TriggersView | undefined {
  if (!view?.startsWith('trigger:')) return undefined;
  if (view === 'trigger:new:app') return { kind: 'app-picker' };
  if (view === 'trigger:new:schedule') return { kind: 'schedule-form' };
  if (view.startsWith('trigger:new:app:'))
    return { kind: 'app-form', event: view.slice('trigger:new:app:'.length) };
  const id = view.slice('trigger:'.length);
  return id && !id.startsWith('new') && id !== 'kinds'
    ? { kind: 'edit', triggerId: id }
    : undefined;
}
